import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { bool, func, shape, string } from 'prop-types';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import * as R from 'ramda';
import { CancelToken } from 'axios';

// Utils
import { capitalize } from 'utils/helpers/format';

// Components
import { MDialog } from 'common';
import Sidebar from './sidebar/Sidebar.connector';
import MobileDrawerConnector from '../mobile-drawer/MobileDrawer.connector';
import CardsPanel from './cards-panel/CardsPanel.connector';
import { setActiveMyAuthTab } from './store/myAuthorizationsSlice';

const MyAuthorizations = ({
  filtersOpen,
  myAuthorizationConfirmationDialog,
  resetFilters,
  setFiltersOpen,
  setMyAuthorizationConfirmationDialog,
  toggleTwoPartyExchange,
  setFilters,
  tempCardFilter,
  resetTempFilters,
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { viewBy, viewById } = useParams();
  const twoPartyCancelToken = useRef();

  const { header, message } = myAuthorizationConfirmationDialog;

  useEffect(() => {
    return () => {
      resetFilters();
      setActiveMyAuthTab('baseProducts');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  useEffect(() => {
    // setting the card filters on initial render
    // once this is done, we can then check the data to set the active tab
    // this is done in fetchMyAuthorizations so we don't have an initial render of the wrong tab
    if (tempCardFilter.location) {
      setFilters({
        name: 'location',
        value: tempCardFilter.location,
      });
    }

    if (tempCardFilter.product) {
      setFilters({
        name: 'product',
        value: tempCardFilter.product,
      });
    }

    resetTempFilters();
    // using tempCardFilter values in the dependency array
    // this ensures when clicking on a notification inside my auth, the correct tab is selected
    // redux functions kept in dep array, they will not change memory locations
  }, [tempCardFilter.location, tempCardFilter.product, resetTempFilters, setFilters]);

  const handleConfirm = () => {
    if (twoPartyCancelToken.current?.cancel) {
      twoPartyCancelToken.current.cancel();
    }
    const cancelToken = CancelToken.source();
    twoPartyCancelToken.current = cancelToken;
    toggleTwoPartyExchange(cancelToken.token);
  };

  return (
    <>
      <Sidebar />
      {lgUp ? (
        <CardsPanel viewBy={viewBy} />
      ) : (
        <MobileDrawerConnector
          activationClick={() => {}}
          filtersClick={() => setFiltersOpen(!filtersOpen)}
          isSequencingWizardEnabled={false}
          panel={<CardsPanel viewBy={viewBy} viewById={viewById} />}
          type={capitalize(viewBy)}
        />
      )}
      <MDialog
        header={header}
        leftButtonAction={() => setTimeout(() => setMyAuthorizationConfirmationDialog({}), 500)}
        leftButtonText="Back"
        onClose={() => setTimeout(() => setMyAuthorizationConfirmationDialog({}), 500)}
        open={!R.isEmpty(myAuthorizationConfirmationDialog)}
        rightButtonAction={() => handleConfirm()}
        rightButtonText="Confirm"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          {message}
        </Typography>
      </MDialog>
    </>
  );
};

MyAuthorizations.propTypes = {
  filtersOpen: bool.isRequired,
  myAuthorizationConfirmationDialog: shape({}).isRequired,
  resetFilters: func.isRequired,
  resetTempFilters: func.isRequired,
  setFilters: func.isRequired,
  setFiltersOpen: func.isRequired,
  setMyAuthorizationConfirmationDialog: func.isRequired,
  tempCardFilter: shape({ location: string, product: string }),
  toggleTwoPartyExchange: func.isRequired,
};
MyAuthorizations.defaultProps = {
  tempCardFilter: { location: '', product: '' },
};

export default MyAuthorizations;
